import { z } from 'zod';

import {
  RefactorActionSchema,
  WhatIfActionSchema,
  WhatIfMetricsSchema,
} from './graph';

// ---------------------------------------------------------------------------
// Structural interfaces for analysis input (PR data from any source)
// ---------------------------------------------------------------------------

/**
 * Minimal review shape accepted by workflow analysis functions.
 * Both GhPrWithReviews.reviews and ConsolidatedPullRequest.reviews
 * satisfy this interface.
 */
export interface AnalyzableReview {
  readonly id: string;
  readonly state: string;
  readonly submittedAt: string | null;
  readonly author: string | { readonly login: string };
}

/**
 * Structural interface for PR data consumed by workflow analysis.
 *
 * Designed so that both GhPrWithReviews (gh CLI) and
 * ConsolidatedPullRequest satisfy this shape, enabling incremental
 * migration from source-specific types to consolidated types.
 */
export interface AnalyzablePullRequest {
  readonly number: number;
  readonly title: string;
  readonly state: string;
  readonly createdAt: string;
  readonly mergedAt: string | null;
  readonly author: string | { readonly login: string };
  readonly reviews: readonly AnalyzableReview[];
  readonly body: string;
  readonly headRefName: string;
}

/**
 * Commit author shape for AI tool detection.
 */
export interface AnalyzableCommitAuthor {
  readonly name: string;
  readonly email: string;
}

/**
 * Commit shape for AI tool detection (needs messageBody for co-author parsing).
 */
export interface AnalyzableCommit {
  readonly oid: string;
  readonly messageHeadline: string;
  readonly messageBody: string;
  readonly authoredDate: string;
  readonly committedDate: string;
  readonly authors: readonly AnalyzableCommitAuthor[];
}

/**
 * PR with commits + reviews for AI tool detection.
 * Standalone interface (not extending AnalyzablePullRequest) because
 * AI detection doesn't need body/headRefName.
 */
export interface AnalyzableCommitPullRequest {
  readonly number: number;
  readonly title: string;
  readonly state: string;
  readonly createdAt: string;
  readonly mergedAt: string | null;
  readonly author: string | { readonly login: string };
  readonly additions: number;
  readonly deletions: number;
  readonly changedFiles: number;
  readonly commits: readonly AnalyzableCommit[];
  readonly reviews: readonly AnalyzableReview[];
  readonly files: readonly { readonly path: string }[];
}

/**
 * Extract login string from an AnalyzablePullRequest/AnalyzableReview author.
 * Handles both `string` (consolidated) and `{ login: string }` (gh CLI) shapes.
 */
export function getAuthorLogin(
  author: string | { readonly login: string },
): string {
  return typeof author === 'string' ? author : author.login;
}

/**
 * `submittedAt` ordering for review recency: a null (pending) submission sorts
 * before any real one. Kept here (analysis layer) so the review-network edges and
 * the work-items reviewer set share one notion of "later review".
 */
function isLaterSubmission(a: string | null, b: string | null): boolean {
  if (a === null) return false;
  if (b === null) return true;
  return Date.parse(a) > Date.parse(b);
}

/**
 * The distinct reviewers on a PR, each kept with their terminal (latest) review —
 * a reviewer who reviewed more than once is counted once, by their most recent
 * submission; the PR author's own reviews are excluded. Shared by the
 * review-network edge build (`review-edges.terminalReviews`) and the work-items
 * reviewer set, so both surfaces agree on "who reviewed this".
 */
export function distinctTerminalReviewers(
  reviews: readonly AnalyzableReview[],
  authorLogin: string,
): Map<string, AnalyzableReview> {
  const terminal = new Map<string, AnalyzableReview>();
  for (const review of reviews) {
    const reviewer = getAuthorLogin(review.author);
    if (reviewer === authorLogin) continue;
    const previous = terminal.get(reviewer);
    if (
      previous === undefined ||
      isLaterSubmission(review.submittedAt, previous.submittedAt)
    ) {
      terminal.set(reviewer, review);
    }
  }
  return terminal;
}

// --- Remediation roadmap schemas ---

export const RemediationStepSchema = z.object({
  stepNumber: z.number(),
  action: WhatIfActionSchema,
  sourceAction: RefactorActionSchema,
  cumulativeMetrics: WhatIfMetricsSchema,
  unlockedActions: z.array(RefactorActionSchema),
  resolvedActions: z.array(RefactorActionSchema),
});

export const RemediationRoadmapSchema = z.object({
  initialMetrics: WhatIfMetricsSchema,
  steps: z.array(RemediationStepSchema),
});

// --- Workstream schemas ---

// Locale-neutral stable keys — UI translates these via i18n rather than
// consuming the English labels directly off the contract.
export const WorkstreamThemeSchema = z.enum([
  'stability',
  'clarity',
  'modularity',
  'test_coverage',
  'knowledge_distribution',
  'mixed',
]);

export const WorkstreamSchema = z.object({
  name: z.string(),
  theme: WorkstreamThemeSchema,
  modules: z.array(z.string()),
  actions: z.array(RefactorActionSchema),
  totalPriority: z.number(),
  estimatedDays: z.number(),
  recommendedOwner: z.string().optional(),
  estimatedMonthlyCost: z.number().optional(),
});

export const WorkstreamResultSchema = z.object({
  workstreams: z.array(WorkstreamSchema),
  totalActionCount: z.number(),
});

// --- Temporal comparison schemas (P6) ---

export const TemporalComparisonSchema = z.object({
  before: WhatIfMetricsSchema,
  after: WhatIfMetricsSchema,
  delta: WhatIfMetricsSchema,
  percentChange: WhatIfMetricsSchema,
  beforeDate: z.string(),
  afterDate: z.string(),
  summary: z.string(),
});

// --- Workflow Intelligence schemas (P31) ---

export const CycleTimeStageSchema = z.enum([
  'waiting_for_review',
  'in_review',
  'waiting_for_merge',
]);

export const CycleTimeStageBreakdownSchema = z.object({
  stage: CycleTimeStageSchema,
  durationHours: z.number(),
});

export const PrCycleTimeSchema = z.object({
  prNumber: z.number(),
  title: z.string(),
  author: z.string(),
  state: z.string(),
  createdAt: z.string(),
  mergedAt: z.string().nullable(),
  totalHours: z.number().nullable(),
  stages: z.array(CycleTimeStageBreakdownSchema),
  slowestStage: CycleTimeStageSchema.nullable(),
  firstReviewAt: z.string().nullable(),
  approvedAt: z.string().nullable(),
});

export const CycleTimeResultSchema = z.object({
  prCycleTimes: z.array(PrCycleTimeSchema),
  teamMedianTotalHours: z.number(),
  teamAvgTotalHours: z.number(),
  stageMedians: z.record(z.string(), z.number()),
  slowestStageOverall: CycleTimeStageSchema.nullable(),
  totalPrsAnalysed: z.number(),
  mergedPrsAnalysed: z.number(),
  lookbackDays: z.number(),
});

export const PrIssueLinkageSchema = z.object({
  prNumber: z.number(),
  prTitle: z.string(),
  linkedIssueIds: z.array(z.string()),
  linkSource: z.enum(['body_reference', 'branch_match']),
});

export const WorkItemRelationshipSchema = z.object({
  itemA: z.object({ id: z.string(), type: z.enum(['pr', 'issue']) }),
  itemB: z.object({ id: z.string(), type: z.enum(['pr', 'issue']) }),
  relationshipType: z.enum(['addresses', 'overlaps', 'blocks']),
  strength: z.number(),
});

export const WorkPathItemSchema = z.object({
  id: z.string(),
  type: z.enum(['pr', 'issue']),
  title: z.string(),
  author: z.string(),
  priorityScore: z.number(),
  blastRadius: z.number(),
  overlapCount: z.number(),
  reviewerAvailabilityHours: z.number().nullable(),
  ageDays: z.number(),
  rationale: z.string(),
});

export const WorkPathResultSchema = z.object({
  orderedItems: z.array(WorkPathItemSchema),
  relationships: z.array(WorkItemRelationshipSchema),
  prIssueLinkages: z.array(PrIssueLinkageSchema),
  computedAt: z.string(),
});

export const WorkflowIntelligenceResultSchema = z.object({
  cycleTime: CycleTimeResultSchema,
  workPath: WorkPathResultSchema,
  lookbackDays: z.number(),
  analysedAt: z.string(),
});

// --- Habit Nudge schemas (P14) ---

export const NudgeCategorySchema = z.enum([
  'stale_work',
  'review_bottleneck',
  'load_imbalance',
  'overlap_conflict',
  'duplicate_work',
  'cycle_time',
  'dependency_order',
  'incident_risk',
]);

export const NudgeSeveritySchema = z.enum([
  'critical',
  'high',
  'medium',
  'low',
]);

export const NudgeRelatedItemSchema = z.object({
  id: z.string(),
  type: z.enum(['pr', 'issue', 'user']),
  title: z.string(),
  url: z.string().optional(),
});

export const NudgeDependencyEdgeSchema = z.object({
  fromId: z.string(),
  toId: z.string(),
  relationship: z.enum(['addresses', 'overlaps', 'blocks']),
});

export const NudgeSchema = z.object({
  id: z.string(),
  category: NudgeCategorySchema,
  severity: NudgeSeveritySchema,
  title: z.string(),
  description: z.string(),
  action: z.string(),
  relatedItems: z.array(NudgeRelatedItemSchema),
  dependencies: z.array(NudgeDependencyEdgeSchema),
  sourceTab: z.enum([
    'friction',
    'workflow',
    'contributors',
    'duplicates',
    'observability',
  ]),
  estimatedHoursLost: z.number(),
  generatedAt: z.string(),
});

export const NudgeEngineResultSchema = z.object({
  nudges: z.array(NudgeSchema),
  workStreams: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      items: z.array(WorkPathItemSchema),
      relationships: z.array(WorkItemRelationshipSchema),
      isCriticalPath: z.boolean(),
    }),
  ),
  generatedAt: z.string(),
});

// --- Duplicate & overlap detection schemas (P24) ---

export const IssueDuplicateReferenceSchema = z.object({
  id: z.string(),
  identifier: z.string(),
  title: z.string(),
  source: z.enum(['github', 'linear']),
  url: z.string(),
});

export const IssueDuplicatePairSchema = z.object({
  issueA: IssueDuplicateReferenceSchema,
  issueB: IssueDuplicateReferenceSchema,
  similarityScore: z.number(),
  explanation: z.string(),
});

export const MergeUrgencySchema = z.enum(['immediate', 'soon', 'low']);

export const PrOverlapReferenceSchema = z.object({
  number: z.number(),
  title: z.string(),
  author: z.string(),
});

export const PrOverlapResultSchema = z.object({
  prA: PrOverlapReferenceSchema,
  prB: PrOverlapReferenceSchema,
  sharedFiles: z.array(z.string()),
  overlapRatio: z.number(),
  mergeRecommendation: z.string(),
  urgency: MergeUrgencySchema,
});

export const DupeDetectionResponseSchema = z.object({
  issueDuplicates: z.array(IssueDuplicatePairSchema),
  prOverlaps: z.array(PrOverlapResultSchema),
  analysedAt: z.string(),
  issueCount: z.number(),
  issuesAnalysed: z.number(),
  issuesTruncated: z.boolean(),
  prCount: z.number(),
});

// --- Friction map schemas (P13) ---

export const FrictionSourceKindSchema = z.enum([
  'slow_first_review',
  'review_iteration',
  'stale_prs',
  'abandoned_prs',
  'rework_churn',
  'blast_radius',
  'knowledge_silo',
]);

export const FrictionCategorySchema = z.enum(['process', 'code']);

export const FrictionContributorSchema = z.object({
  login: z.string(),
  avgHours: z.number(),
  count: z.number(),
});

export const FrictionTargetSchema = z.object({
  nodeId: z.string(),
  name: z.string(),
  score: z.number(),
});

export const FrictionEntrySchema = z.object({
  kind: FrictionSourceKindSchema,
  category: FrictionCategorySchema,
  severity: z.enum(['critical', 'high', 'medium', 'low']),
  estimatedWeeklyHours: z.number(),
  explanation: z.string(),
  recommendedAction: z.string(),
  topContributors: z.array(FrictionContributorSchema),
  topTargets: z.array(FrictionTargetSchema),
  affectedCount: z.number(),
});

export const FrictionMapResultSchema = z.object({
  entries: z.array(FrictionEntrySchema),
  totalWeeklyHoursLost: z.number(),
  totalPrsAnalysed: z.number(),
  lookbackDays: z.number(),
  analysedAt: z.string(),
  hasProcessData: z.boolean(),
  hasCodeData: z.boolean(),
});

// --- Code hotspots response schemas ---

export const SymbolHotspotSchema = z.object({
  id: z.string(),
  name: z.string(),
  symbolType: z.string(),
  path: z.string(),
  fanIn: z.number(),
  commitCount6m: z.number(),
  score: z.number(),
  confidence: z.enum(['high', 'medium', 'low']).optional(),
});

export const DeadFileResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  path: z.string(),
  confidence: z.enum(['high', 'medium', 'low']),
  rationale: z.string(),
});

export const DeadModuleResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  path: z.string(),
  nodeType: z.string(),
  confidence: z.enum(['high', 'medium', 'low']),
  rationale: z.string(),
});

export const DeadCodeSummaryResponseSchema = z.object({
  totalDead: z.number().int().nonnegative(),
  deadModuleCount: z.number().int().nonnegative(),
  deadFileCount: z.number().int().nonnegative(),
  deadSymbolCount: z.number().int().nonnegative(),
  entryPointCount: z.number().int().nonnegative(),
  reachabilityPercentage: z.number().min(0).max(100),
});

export const CodeHotspotsResponseSchema = z.object({
  functionHotspots: z.array(SymbolHotspotSchema),
  highFanIn: z.array(SymbolHotspotSchema),
  deadFunctions: z.array(SymbolHotspotSchema),
  deadFiles: z.array(DeadFileResponseSchema),
  deadModules: z.array(DeadModuleResponseSchema),
  deadCodeSummary: DeadCodeSummaryResponseSchema.optional(),
});
